import { Button, Spinner, type ButtonProps } from "@fluentui/react-components";
import { ArrowDownloadRegular } from "@fluentui/react-icons";
import { getAccessTokenUsingMsal, useMsal } from "@skanska/vsa-client-auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiConfig } from "../../config/AppConfig";
import { loginRequest } from "../../config/MsalConfig";
import { ApiEndpoints } from "../../models/ApiEndpoints";
import { Tool, ToolTypes } from "../../models/Tool";
import { getToolType } from "../../services/ToolService";

interface ToolDownloadButtonProps {
  tool: Tool;
  showLabel?: boolean;
  size?: ButtonProps["size"];
  isDraft?: boolean;
}

const ToolDownloadButton: React.FC<ToolDownloadButtonProps> = ({
  tool,
  showLabel = false,
  size = "medium",
  isDraft = false,
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { instance, inProgress } = useMsal();

  const handleDownloadClick = async () => {
    if (!tool.versionId) {
      return;
    }

    setLoading(true);

    try {
      const accessToken = await getAccessTokenUsingMsal(
        inProgress,
        instance,
        loginRequest.scopes,
        apiConfig.tenantId
      );

      const endpoint = isDraft
        ? ApiEndpoints.ToolDraftDownload
        : ApiEndpoints.ToolOriginalDownload;

      const response = await fetch(
        `${apiConfig.apiUrl}/${endpoint}/${tool.versionId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const downloadUrl = await response.text();
      const link = document.createElement("a");

      link.href = downloadUrl;
      link.setAttribute("download", "");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download original file", error);
    } finally {
      setLoading(false);
    }
  };

  if (getToolType(tool) !== ToolTypes.Document) {
    // Only show for documents
    return null;
  }

  return (
    <Button
      type="button"
      icon={loading ? <Spinner size="tiny" /> : <ArrowDownloadRegular />}
      size={size}
      onClick={handleDownloadClick}
      disabled={loading || !tool.versionId}
      title={t("downloadOriginalFile")}
      aria-label={!showLabel ? t("downloadOriginalFile") : undefined}
    >
      {showLabel && t("downloadOriginalFile")}
    </Button>
  );
};

export default ToolDownloadButton;
